import { SystemError } from '../../../errors';
import type { Boost } from '../descriptor';
import type { ElemPredicate, Predicate, Scalar } from '../predicate';

const NEVER_FILTER = 'id:=__never__';
const SAFE_FIELD = /^[A-Za-z_][A-Za-z0-9_.]*$/;
const SAFE_BARE_VALUE = /^[A-Za-z0-9_.-]+$/;

const backtick = (value: string): string => '`' + value + '`';

const field = (value: string): string => {
  if (SAFE_FIELD.test(value)) return value;

  throw new SystemError({
    code: 'SEARCH_FILTER_UNSUPPORTED_FIELD_NAME',
    userMessage: 'Typesense filters cannot address the field name "' + value + '"; it has no quoting syntax.',
    retryable: false,
  });
};

const finiteNumber = (value: number): string => {
  if (!Number.isFinite(value)) {
    throw new SystemError({
      code: 'SEARCH_FILTER_NON_FINITE_NUMBER',
      userMessage: 'Typesense filters require a finite number; received ' + String(value) + '.',
      retryable: false,
    });
  }

  return String(value);
};

const stringValue = (value: string): string => {
  if (SAFE_BARE_VALUE.test(value)) return value;

  if (value === '') {
    throw new SystemError({
      code: 'SEARCH_FILTER_UNSUPPORTED_STRING_VALUE',
      userMessage: 'Typesense filters cannot express an empty string value.',
      retryable: false,
    });
  }

  if (value.includes('`')) {
    throw new SystemError({
      code: 'SEARCH_FILTER_UNSUPPORTED_STRING_VALUE',
      userMessage: 'Typesense filters cannot escape a backtick inside a value; received "' + value + '".',
      retryable: false,
    });
  }

  return backtick(value);
};

const scalar = (value: Scalar): string => {
  if (typeof value === 'number') return finiteNumber(value);
  if (typeof value === 'boolean') return String(value);

  return stringValue(value);
};

function assertNonEmptyInList<T>(predicate: Extract<Predicate<T>, { kind: 'in' }>): void {
  if (predicate.values.length === 0) {
    throw new SystemError({
      code: 'SEARCH_FILTER_EMPTY_IN_LIST',
      userMessage: 'Typesense filters cannot compile an empty IN list.',
      retryable: false,
    });
  }
}

function assertNegatable<T>(
  operand: Predicate<T>
): asserts operand is Extract<Predicate<T>, { kind: 'eq' | 'eqStringLiteral' | 'in' }> {
  if (operand.kind === 'eq' || operand.kind === 'eqStringLiteral' || operand.kind === 'in') return;

  if (operand.kind === 'and' || operand.kind === 'or') {
    throw new SystemError({
      code: 'SEARCH_FILTER_UNSUPPORTED_NEGATED_GROUP',
      userMessage: 'Typesense filters do not support NOT applied to an ' + operand.kind.toUpperCase() + ' group.',
      retryable: false,
    });
  }

  throw new SystemError({
    code: 'SEARCH_FILTER_UNSUPPORTED_NEGATION',
    userMessage:
      'Typesense filters can only negate an eq, eqStringLiteral or in predicate; received ' + operand.kind + '.',
    retryable: false,
  });
}

const equalityValue = <T>(predicate: Extract<Predicate<T>, { kind: 'eq' | 'eqStringLiteral' | 'neq' }>): string =>
  predicate.kind === 'eqStringLiteral' ? backtick(predicate.value) : scalar(predicate.value);

const atom = <T>(
  predicate: Extract<Predicate<T>, { kind: 'eq' | 'eqStringLiteral' | 'neq' }>,
  operator: ':=' | ':!='
): string => field(predicate.field) + operator + equalityValue(predicate);

const list = <T>(predicate: Extract<Predicate<T>, { kind: 'in' }>, operator: ':=' | ':!='): string =>
  field(predicate.field) + operator + '[' + predicate.values.map((value) => scalar(value)).join(',') + ']';

const comparison = <T>(predicate: Extract<Predicate<T>, { kind: 'cmp' | 'pathCmp' }>): string =>
  field(predicate.kind === 'pathCmp' ? predicate.path.join('.') : predicate.field) +
  ':' +
  predicate.op +
  finiteNumber(predicate.value);

type ElemClause = Exclude<ElemPredicate, { kind: 'and' }>;

const stringLiteral = (value: string): string => {
  stringValue(value);
  return backtick(value);
};

const elemScalar = (value: Scalar): string => (typeof value === 'string' ? stringLiteral(value) : scalar(value));

const elemClauses = (where: ElemPredicate): readonly ElemClause[] =>
  where.kind === 'and' ? where.operands.flatMap(elemClauses) : [where];

const elemClause = (clause: ElemClause): string =>
  clause.kind === 'eq'
    ? field(clause.field) + ':=' + elemScalar(clause.value)
    : field(clause.field) + ':' + clause.op + finiteNumber(clause.value);

const elemMatchFilter = <T>(predicate: Extract<Predicate<T>, { kind: 'elemMatch' }>): string => {
  const clauses = elemClauses(predicate.where);
  if (clauses.length === 0) {
    throw new SystemError({
      code: 'SEARCH_FILTER_EMPTY_ELEM_MATCH',
      userMessage: 'Typesense filters cannot compile an empty element predicate.',
      retryable: false,
    });
  }

  return field(predicate.field) + '.{' + clauses.map(elemClause).join(' && ') + '}';
};

const negated = <T>(operand: Predicate<T>): string => {
  assertNegatable(operand);

  return operand.kind === 'in' ? list(operand, ':!=') : atom(operand, ':!=');
};

const validate = <T>(predicate: Predicate<T>): void => {
  switch (predicate.kind) {
    case 'always':
    case 'never':
      return;
    case 'eqStringLiteral':
      field(predicate.field);
      if (predicate.value === '' || predicate.value.includes('`')) stringValue(predicate.value);
      return;
    case 'eq':
    case 'neq':
      field(predicate.field);
      scalar(predicate.value);
      return;
    case 'cmp':
      field(predicate.field);
      finiteNumber(predicate.value);
      return;
    case 'pathCmp':
      field(predicate.path.join('.'));
      finiteNumber(predicate.value);
      return;
    case 'in':
      field(predicate.field);
      assertNonEmptyInList(predicate);
      predicate.values.forEach((value) => scalar(value));
      return;
    case 'elemMatch':
      elemMatchFilter(predicate);
      return;
    case 'not':
      validate(predicate.operand);
      assertNegatable(predicate.operand);
      return;
    case 'and':
    case 'or':
      predicate.operands.forEach((operand) => validate(operand));
  }
};

const group = (rendered: readonly string[], separator: ' && ' | ' || ', isNested: boolean): string => {
  if (rendered.length === 0) return '';
  if (rendered.length === 1) return rendered[0] ?? '';

  const joined = rendered.join(separator);
  return isNested ? '(' + joined + ')' : joined;
};

const never = (): string => NEVER_FILTER;

const renderOr = <T>(operands: readonly Predicate<T>[], isNested: boolean): string => {
  if (operands.some((operand) => operand.kind === 'always')) return '';

  const renderedOperands = operands
    .filter((operand) => operand.kind !== 'never')
    .map((operand) => render(operand, true))
    .filter((rendered) => rendered !== '');

  return renderedOperands.length === 0 ? never() : group(renderedOperands, ' || ', isNested);
};

const renderAnd = <T>(operands: readonly Predicate<T>[], isNested: boolean): string => {
  if (operands.some((operand) => operand.kind === 'never')) return never();

  const renderedOperands = operands.map((operand) => render(operand, true)).filter((rendered) => rendered !== '');

  return group(renderedOperands, ' && ', isNested);
};

const render = <T>(predicate: Predicate<T>, isNested: boolean): string => {
  switch (predicate.kind) {
    case 'always':
      return '';
    case 'never':
      return never();
    case 'eq':
    case 'eqStringLiteral':
      return atom(predicate, ':=');
    case 'neq':
      return atom(predicate, ':!=');
    case 'cmp':
    case 'pathCmp':
      return comparison(predicate);
    case 'in':
      return list(predicate, ':=');
    case 'elemMatch':
      return elemMatchFilter(predicate);
    case 'not':
      return negated(predicate.operand);
    case 'and':
      return renderAnd(predicate.operands, isNested);
    case 'or':
      return renderOr(predicate.operands, isNested);
  }
};

/** Prefix/typo expansions per query token; the server default of 4 reaches 4 of 43 `004…` order numbers. */
export const TYPESENSE_MAX_CANDIDATES = 100;

export const TYPESENSE_MAX_PER_PAGE = 250;

export const toTypesenseFilter = <T>(predicate: Predicate<T>): string => {
  validate(predicate);
  return render(predicate, false);
};

const hasBoostValue = (value: Scalar | null | undefined): value is Scalar =>
  value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '');

export const toTypesenseBoostSort = <T>(boosts: readonly Boost<T>[]): string => {
  const clauses = boosts.flatMap((boost) =>
    hasBoostValue(boost.value)
      ? ['(' + field(boost.field) + ':=' + scalar(boost.value) + '):' + finiteNumber(boost.weight)]
      : []
  );
  return clauses.length === 0 ? '' : '_eval([' + clauses.join(',') + ']):desc';
};
